import React, { useState, useEffect, useMemo} from "react";
import { useNavigate } from "react-router-dom";
import CorporateHeader from "../../components/corporatedashboard/CorporateHeader";
import HealthWorkerDashboardSidebar from "../../components/global/HealthWorkerSidebar";
import { BsSearch, BsArrowUp, BsArrowDown, BsFilter, BsDownload } from "react-icons/bs";
import { useStateProvider } from "../../manager/StateContext";
import axios from "axios";

const AdvancedDataTable = () => {
  const [{ stage_token }] = useStateProvider();
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setOpen] = useState(false);

  const navigate = useNavigate();


  // Table configuration
  const columns = [
    { key: "name", label: "Name", sortable: true, render: (row) => `${row.name}`.trim(), filterable: true, type: "string" },
    { key: "contractId", label: "Contract Id", sortable: true, filterable: true, type: "string" },
    // { key: "amount", label: "Amount", sortable: true, filterable: true,  render: (row) => row.formattedAmount, type: "number" },
    { key: "date", label: "Date", sortable: true, filterable: true, type: "date" },
    { key: "status", label: "Status", sortable: true, filterable: true,  render: (row) => row.status, type: "string" },
    { key: "action", label: "Action", sortable: false, filterable: false, type: "string" }
  ];

  // Table state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch data
  const fetchHomeCareAppointments = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_LIVE_URL}/dashboard/homecare-booking`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stage_token}`,
          },
        }
      );
      setAppointments(response.data);
      console.log('data', response.data)
    } catch (err) {
      console.error("Error fetching appointments:", err);
      setError("Failed to fetch appointments. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeCareAppointments();
  }, []);

  // Transform data for display
  const transformedData = useMemo(() => {
    return appointments.map((appointment) => {

      const statusValue = appointment.contract_status;

      let statusDisplay;
      let statusClass = "";

     

      switch (statusValue) {
        
        case '0':
          statusDisplay = "Completed";
          statusClass = "bg-green-100 text-green-500";
          break;
        case '1':	
          statusDisplay = "In Progress";
          statusClass = "bg-blue-100 text-blue-500";
          break;
        case '2':	
          statusDisplay = "Pending";
          statusClass = "bg-red-100 text-red-500";
          break;
          default:
            statusDisplay = "Unknown";
            statusClass = "bg-gray-100 text-gray-800";
      }

      return {
        id: appointment.id,
        name: `${appointment.patient?.first_name || ''} ${appointment.patient?.last_name || ''}`.trim() || "N/A",
        contractId: appointment.contract_id || "N/A",
        // amount: appointment.consultation_price || 0,
        // formattedAmount: appointment.consultation_price
        //   ? `₦${Number(appointment.consultation_price).toLocaleString('en-NG')}`
        //   : "₦N/A",
        date: appointment.date || "N/A",
        status: (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}>
            {statusDisplay}
          </span>
        ),
        rawStatus: statusValue,
        action: (
          <button
          onClick={() => navigate(`/dashboard/homecare-appointment/${appointment.contract_id}`)}
          className="text-blue-500 hover:text-blue-700"
        >
          View
        </button>
        ),
      };
    });
  }, [appointments]);



  // Handle view action
  const handleView = (id) => {
    console.log("View appointment:", id);
    // Implement view logic here
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Handle export to CSV
  const handleExport = () => {
    const headers = columns.map(col => col.label).join(",");
    const rows = filteredData.map(row => 
      columns.map(col => `"${row[col.key]}"`).join(",")
    ).join("\n");
    
    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "appointments.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter and sort data
  const filteredData = useMemo(() => {
    let result = [...transformedData];
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        columns.some(col => 
          String(item[col.key]).toLowerCase().includes(query)
        )
      );
    }
    
    // Apply sorting
    if (sortConfig.key) {
      const column = columns.find(col => col.key === sortConfig.key);
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        
        if (column?.type === "date") {
          return sortConfig.direction === "asc" 
            ? new Date(aVal) - new Date(bVal) 
            : new Date(bVal) - new Date(aVal);
        }
        
        if (column?.type === "number") {
          return sortConfig.direction === "asc" 
            ? Number(aVal) - Number(bVal) 
            : Number(bVal) - Number(aVal);
        }
        
        return sortConfig.direction === "asc" 
          ? String(aVal).localeCompare(String(bVal)) 
          : String(bVal).localeCompare(String(aVal));
      });
    }
    
    return result;
  }, [transformedData, searchQuery, sortConfig, columns]);

  // Pagination logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // Adjust current page if it's out of bounds
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const maxVisiblePages = 5;
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  if (isLoading) {
    return (
      <div className="flex justify-between bg-[#F9F9F9] font-Montserrat">
        <HealthWorkerDashboardSidebar isOpen={isOpen} setOpen={setOpen} />
        <div className="sm:w-screen space-x-2 h-screen overflow-y-auto custom-scrollbar">
          <CorporateHeader isOpen={isOpen} setOpen={setOpen} />
          <div className="container mx-auto p-6">
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-between bg-[#F9F9F9] font-Montserrat">
        <HealthWorkerDashboardSidebar isOpen={isOpen} setOpen={setOpen} />
        <div className="sm:w-screen space-x-2 h-screen overflow-y-auto custom-scrollbar">
          <CorporateHeader isOpen={isOpen} setOpen={setOpen} />
          <div className="container mx-auto p-6">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
              <button 
                onClick={fetchHomeCareAppointments}
                className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between bg-[#F9F9F9] font-Montserrat">
      <HealthWorkerDashboardSidebar isOpen={isOpen} setOpen={setOpen} />
      <div className="sm:w-screen space-x-2 h-screen overflow-y-auto custom-scrollbar">
        <CorporateHeader isOpen={isOpen} setOpen={setOpen} />
        <div className="container mx-auto p-6">
          {/* Search and Controls */}
          <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <BsSearch className="absolute left-3 top-3 text-gray-400" />
            </div>
            <div className="flex gap-4 items-center">
              <select
                className="border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[10, 25, 50, 100].map(size => (
                  <option key={size} value={size}>{size} per page</option>
                ))}
              </select>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                disabled={filteredData.length === 0}
              >
                <BsDownload /> Export CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-500">
                <tr>
                  {columns.map(column => (
                    <th
                      key={column.key}
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        column.sortable ? "cursor-pointer" : ""
                      }`}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center gap-2 text-white font-Poppins font-medium">
                        {column.label}
                        {column.sortable && (
                          <span className="text-white">
                            {sortConfig.key === column.key ? (
                              sortConfig.direction === "asc" ? <BsArrowUp /> : <BsArrowDown />
                            ) : (
                              <BsFilter />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {columns.map((column) => (
                        <td
                          key={`${row.id}-${column.key}`}
                          className="px-6 py-4 whitespace-nowrap capitalize text-sm text-gray-900 font-Roboto"
                        >
                          {column.render ? column.render(row) : row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-4 text-center text-gray-500">
                      {searchQuery ? "No matching appointments found" : "No appointments available"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className="text-sm text-gray-500 mb-4 sm:mb-0">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                >
                  Previous
                </button>
                {getPageNumbers().map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border rounded-lg ${
                      currentPage === pageNum ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedDataTable;